import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Volume2, VolumeX, Maximize2, Minimize2, Play, Pause, BookOpen, X,
} from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useVerseOfTheDay } from "@/lib/votd";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FADE_MS = 1400;
const SLIDE_MS = 5500;
const CONTROLS_HIDE_MS = 2500;   // Auto-hide idle timeout for video chrome
const VOTD_SWIPE_THRESHOLD = 60; // px of horizontal swipe to dismiss

export default function ChurchHero({
  church,
  eyebrow,
  title,
  subtitle,
  height = "h-[290px]",
  testId = "church-hero",
  isLive = false,
  onLiveTap,
}) {
  const { t, lang } = useI18n();
  const votd = useVerseOfTheDay(lang);

  const staticSrc = church?.hero_image_url || "/hero/urban-gospel.webp";

  // Single source of truth for hero copy. Callers (Dashboard / Landing /
  // Admin panel) intentionally do NOT pre-compute fallbacks anymore —
  // otherwise the admin's "hide" toggles couldn't take effect. Priority:
  //   1) explicit prop from caller (e.g. AdminPanel's "Admin" eyebrow)
  //   2) admin-authored church field
  //   3) sensible localized default
  // If the matching `hero_hide_*` flag is true, the piece is never
  // rendered, even when text has been typed in — this is how admins
  // completely blank out the eyebrow / title / subtitle.
  const hideEyebrow = Boolean(church?.hero_hide_eyebrow);
  const hideTitle = Boolean(church?.hero_hide_title);
  const hideSubtitle = Boolean(church?.hero_hide_subtitle);

  const displayEyebrow =
    eyebrow || (church?.hero_eyebrow?.trim() || t("welcome"));
  const displayTitle =
    title || (church?.hero_title?.trim() || church?.name || t("appName"));
  // Subtitle default lives in i18n as `heroSubtitle`. If the admin
  // clears the field (empty string) but hasn't ticked "Hide", we fall
  // back to the localized default so brand-new churches still ship with
  // useful copy.
  const displaySubtitle =
    subtitle ?? (church?.hero_subtitle?.trim() || t("heroSubtitle"));

  const mediaType =
    church?.hero_media_type
      || (church?.gallery_in_hero ? "album" : "image");
  const videoSrc = church?.hero_video_url;

  // ---------- Album mode ---------------------------------------------
  const useGallery = mediaType === "album" && Boolean(church?.church_code);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [active, setActive] = useState(0);
  useEffect(() => {
    if (!useGallery) return;
    let cancelled = false;
    axios.get(`${API}/public/church/${church.church_code}/gallery/recent?limit=8`)
      .then((r) => { if (!cancelled) setGalleryPhotos(r.data || []); })
      .catch(() => { if (!cancelled) setGalleryPhotos([]); });
    return () => { cancelled = true; };
  }, [useGallery, church?.church_code]);
  useEffect(() => {
    if (!useGallery || galleryPhotos.length < 2) return;
    const timer = setTimeout(() => {
      setActive((i) => (i + 1) % galleryPhotos.length);
    }, SLIDE_MS);
    return () => clearTimeout(timer);
  }, [useGallery, active, galleryPhotos.length]);

  // ---------- Video mode + auto-hide chrome --------------------------
  const [videoFailed, setVideoFailed] = useState(false);
  const showVideo = mediaType === "video" && Boolean(videoSrc) && !videoFailed;
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);
  const [volume, setVolume] = useState(0.8);
  const [isFs, setIsFs] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const hideTimerRef = useRef(null);

  const scheduleHide = useCallback(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(
      () => setControlsVisible(false),
      CONTROLS_HIDE_MS,
    );
  }, []);
  const nudgeControls = useCallback(() => {
    setControlsVisible(true);
    scheduleHide();
  }, [scheduleHide]);

  useEffect(() => {
    if (!showVideo) return;
    scheduleHide();
    return () => hideTimerRef.current && clearTimeout(hideTimerRef.current);
  }, [showVideo, scheduleHide]);

  // iOS Safari (especially in PWA / standalone mode) sometimes ignores
  // the `autoplay` attribute alone — even with `muted` + `playsInline`
  // — so we explicitly kick playback once the metadata is ready. On
  // Android/desktop this is a no-op because the browser already
  // started playing. Wrapped in try/catch because if a user *does*
  // pause manually and then navigates away/back, we don't want to
  // fight their choice.
  // Reset the fallback flag whenever the source changes so a fresh
  // upload gets a chance to play, even if the previous one had errored.
  useEffect(() => { setVideoFailed(false); }, [videoSrc]);

  useEffect(() => {
    if (!showVideo) return;
    const v = videoRef.current;
    if (!v) return;
    const kick = () => {
      // Belt-and-suspenders: ensure muted at the property level too;
      // iOS Safari < 15 sometimes doesn't honor the HTML attribute
      // alone during first play.
      v.muted = true;
      v.playsInline = true;
      const p = v.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => { /* user gesture required — poster stays visible */ });
      }
    };
    // If metadata is already loaded (fast networks / cached), kick now.
    if (v.readyState >= 1) kick();
    v.addEventListener("loadedmetadata", kick);
    v.addEventListener("canplay", kick);
    return () => {
      v.removeEventListener("loadedmetadata", kick);
      v.removeEventListener("canplay", kick);
    };
  }, [showVideo, videoSrc]);

  useEffect(() => {
    const onFs = () => setIsFs(Boolean(
      document.fullscreenElement || document.webkitFullscreenElement
    ));
    document.addEventListener("fullscreenchange", onFs);
    document.addEventListener("webkitfullscreenchange", onFs);
    return () => {
      document.removeEventListener("fullscreenchange", onFs);
      document.removeEventListener("webkitfullscreenchange", onFs);
    };
  }, []);

  const toggleMute = useCallback((e) => {
    e?.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    nudgeControls();
  }, [nudgeControls]);
  const togglePlay = useCallback((e) => {
    e?.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
    nudgeControls();
  }, [nudgeControls]);
  const onVolume = useCallback((e) => {
    e.stopPropagation();
    const vol = Number(e.target.value);
    setVolume(vol);
    const v = videoRef.current;
    if (v) {
      v.volume = vol;
      if (vol > 0 && v.muted) { v.muted = false; setMuted(false); }
    }
    nudgeControls();
  }, [nudgeControls]);
  const toggleFs = useCallback(async (e) => {
    e?.stopPropagation();
    const el = containerRef.current;
    const v = videoRef.current;
    if (!el) return;
    // iOS Safari doesn't implement `Element.requestFullscreen()` on
    // arbitrary containers — calling it either silently fails or
    // enters a broken pseudo-fullscreen where the browser chrome
    // overlaps the top of the video (this was the "top cut off"
    // report). The correct API on iPhone/iPad is
    // `HTMLVideoElement.webkitEnterFullscreen()` which hands off to
    // the native iOS video player (proper safe-area handling, native
    // controls, AirPlay). We detect support and route accordingly.
    const isIOSFsVideo =
      v && typeof v.webkitEnterFullscreen === "function" &&
      typeof el.requestFullscreen !== "function";
    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      try {
        if (isIOSFsVideo) {
          // Native iOS video fullscreen — no `document.fullscreenElement`
          // event fires, so we don't flip `isFs` for this path. iOS
          // handles the whole UI itself, including the safe area.
          v.webkitEnterFullscreen();
        } else if (typeof el.requestFullscreen === "function") {
          await el.requestFullscreen();
        } else if (typeof el.webkitRequestFullscreen === "function") {
          // Older desktop Safari.
          el.webkitRequestFullscreen();
        }
      } catch { /* ignore — poster / inline playback remains */ }
    } else {
      try {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      } catch { /* ignore */ }
    }
    nudgeControls();
  }, [nudgeControls]);

  // ---------- Verse of the Day — minimized ↔ expanded --------------
  // Video and album modes launch the VOTD in a MINIMIZED pill so the
  // media plays uninterrupted, but the pill still teases today's
  // reference so it invites a tap. Static image mode expands the full
  // verse right away. Swiping the expanded card left/right collapses
  // it back to the pill; tapping the pill re-expands.
  //
  // The `church` prop arrives asynchronously on public pages, so we
  // sync the default from `mediaType` via effect (not just the initial
  // useState value). A `userTouchedVotd` ref stops that auto-sync as
  // soon as the user has interacted, so their choice is never
  // overridden by a downstream prop refresh.
  const votdDefaultMinimized = mediaType === "video" || mediaType === "album";
  const [votdMinimized, setVotdMinimized] = useState(votdDefaultMinimized);
  const userTouchedVotd = useRef(false);
  useEffect(() => {
    if (userTouchedVotd.current) return;
    setVotdMinimized(mediaType === "video" || mediaType === "album");
  }, [mediaType]);
  const setVotdMinimizedByUser = (v) => {
    userTouchedVotd.current = true;
    setVotdMinimized(v);
  };
  const [votdDrag, setVotdDrag] = useState(0);
  const dragRef = useRef({ active: false, startX: 0, startY: 0 });

  const onVotdPointerDown = (e) => {
    if (votdMinimized) return;
    // Ignore the pointerdown coming from the close (×) button — the
    // button's onClick already handles dismiss and we don't want the
    // drag state to swallow the tap on iOS Safari.
    if (e.target?.closest?.("[data-votd-close]")) return;
    dragRef.current = { active: true, startX: e.clientX, startY: e.clientY };
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch { /* iOS Safari occasionally rejects capture — safe to ignore */ }
  };
  const onVotdPointerMove = (e) => {
    if (!dragRef.current.active) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    // Vertical dominance → treat as scroll, cancel the drag.
    if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 12) {
      dragRef.current.active = false;
      setVotdDrag(0);
      return;
    }
    setVotdDrag(dx);
  };
  const onVotdPointerUp = () => {
    if (!dragRef.current.active) return;
    dragRef.current.active = false;
    if (Math.abs(votdDrag) > VOTD_SWIPE_THRESHOLD) setVotdMinimizedByUser(true);
    setVotdDrag(0);
  };
  const dismissVotd = (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    dragRef.current.active = false;
    setVotdDrag(0);
    setVotdMinimizedByUser(true);
  };

  const showChrome = !isFs;
  const showVotd = showChrome && !votdMinimized;

  return (
    <div
      className="relative"
      data-testid={testId}
      ref={containerRef}
      onMouseMove={showVideo ? nudgeControls : undefined}
      onTouchStart={showVideo ? nudgeControls : undefined}
    >
      <div className={`${height} w-full relative overflow-hidden bg-stone-900`}>
        {showVideo ? (
          <video
            ref={videoRef}
            src={videoSrc}
            // Show the admin-configured hero image while the video is
            // fetching + buffering. As soon as the first frame decodes,
            // the browser automatically replaces the poster with the
            // video — so users never see a black rectangle on slow
            // networks. Falls back to the bundled default when no
            // image has been uploaded.
            poster={staticSrc}
            preload="metadata"
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            // iOS < 15 needs the vendor-prefixed inline attribute to
            // avoid auto-launching the fullscreen video player on tap.
            // Also set `muted` at the DOM property level (via the
            // effect above) since some iOS versions ignore the
            // attribute during first mount. These are lowercase HTML
            // attributes, not React props, so pass them via `{...}`.
            {...{ "webkit-playsinline": "true", "x-webkit-airplay": "deny" }}
            disableRemotePlayback
            controls={false}
            onError={() => setVideoFailed(true)}
            data-testid={`${testId}-video`}
          />
        ) : useGallery && galleryPhotos.length > 0 ? (
          galleryPhotos.map((p, i) => (
            <img
              key={p.id}
              src={p.image_url}
              alt=""
              className="absolute inset-0 w-full h-full object-cover animate-ken-burns"
              style={{
                opacity: i === active ? 1 : 0,
                transition: `opacity ${FADE_MS}ms ease-in-out`,
              }}
            />
          ))
        ) : (
          <img
            src={staticSrc}
            alt=""
            className="absolute inset-0 w-full h-full object-cover animate-ken-burns"
          />
        )}
        {/* Bottom gradient only when overlays are showing AND we're in
            static image mode. Video and album (gallery) modes render at
            full opacity so the video/photos aren't dimmed. The heavy
            text shadows on the church name/subtitle keep them readable
            without the dark wash. */}
        {showChrome && !showVideo && !useGallery && (
          <div className="absolute inset-0 bg-gradient-to-b from-stone-900/50 via-stone-900/45 to-stone-900/95 pointer-events-none" />
        )}
      </div>

      {/* VOTD — swipe left/right to dismiss OR tap the × button. On
          iOS Safari swipe gestures can get intercepted by the browser's
          native edge-back gesture, so the explicit close button is a
          reliable fallback for iPhone/iPad users. */}
      {showVotd && (
        <div
          className="absolute left-4 right-4 top-3"
          data-testid={`${testId}-votd`}
          onPointerDown={onVotdPointerDown}
          onPointerMove={onVotdPointerMove}
          onPointerUp={onVotdPointerUp}
          onPointerCancel={onVotdPointerUp}
          style={{
            transform: votdDrag ? `translateX(${votdDrag}px)` : undefined,
            opacity: votdDrag ? Math.max(0.3, 1 - Math.abs(votdDrag) / 200) : 1,
            transition: dragRef.current.active ? "none" : "transform 260ms ease, opacity 260ms ease",
            cursor: "grab",
            touchAction: "none",
          }}
        >
          <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl pl-4 pr-9 py-3 text-white shadow-md select-none relative">
            <button
              type="button"
              data-votd-close
              data-testid={`${testId}-votd-close`}
              onClick={dismissVotd}
              onPointerDown={(e) => e.stopPropagation()}
              aria-label="Dismiss verse of the day"
              className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full grid place-items-center text-white/85 hover:bg-white/15 active:bg-white/25 transition"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="text-[10px] uppercase tracking-[0.15em] font-semibold opacity-90 flex items-center justify-between gap-2">
              <span>{t("todayVerse")} · {votd.ref}</span>
              <span className="text-[9px] opacity-60 normal-case tracking-normal">swipe or tap ×</span>
            </div>
            <p className="font-serif-display text-[13px] leading-snug mt-1 line-clamp-3 drop-shadow">
              {votd.text}
            </p>
          </div>
        </div>
      )}

      {/* Minimized state — plain "Verse of the day" label. No reference,
          no "tap to read" hint; a single tap still expands to the full
          card. Applies to video and photo-album hero modes. */}
      {showChrome && votdMinimized && votd?.ref && (
        <button
          type="button"
          onClick={() => setVotdMinimizedByUser(false)}
          data-testid={`${testId}-votd-restore`}
          aria-label={t("todayVerse")}
          className="absolute top-3 left-4 rounded-full bg-black/40 backdrop-blur-sm border border-white/15 px-2.5 py-1 text-[10px] font-semibold text-white flex items-center gap-1 hover:bg-black/55 transition"
        >
          <BookOpen className="w-3 h-3" /> {t("todayVerse")}
        </button>
      )}

      {/* Live-service pill — mirrors the Verse-of-the-Day pill on the
          opposite corner so both chips sit on the same visual line
          when VOTD is minimized. In static-image mode VOTD renders as
          a full-width card at the top, so we drop the live pill
          below it to avoid overlap. Only rendered when a service is
          actually broadcasting (parent passes `isLive`). */}
      {showChrome && isLive && (
        <button
          type="button"
          onClick={onLiveTap}
          data-testid={`${testId}-live-pill`}
          aria-label={t("liveNow") || "Service is live now"}
          className={`absolute right-4 rounded-full bg-red-600 border border-white/20 px-2.5 py-1 text-[10px] font-bold text-white flex items-center gap-1.5 shadow-lg hover:bg-red-700 transition tracking-wide z-20 ${
            votdMinimized ? "top-3" : "top-[6.5rem]"
          }`}
        >
          <span className="relative flex h-1.5 w-1.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-300 opacity-75" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
          </span>
          {t("liveNow") || "LIVE NOW"}
        </button>
      )}

      {showChrome && (
        <div className="absolute inset-x-0 bottom-0 px-6 pb-6 pointer-events-none">
          {!hideEyebrow && (
            <div className="label-eyebrow text-white drop-shadow-md" style={{ textShadow: "0 2px 8px rgba(0,0,0,0.85)" }}>
              {displayEyebrow}
            </div>
          )}
          {!hideTitle && (
            <h1
              className={`font-serif-display text-white text-xl sm:text-2xl leading-tight tracking-tight ${hideEyebrow ? "" : "mt-1"}`}
              style={{ textShadow: "0 2px 10px rgba(0,0,0,0.9), 0 1px 2px rgba(0,0,0,0.9)" }}
            >
              {displayTitle}
            </h1>
          )}
          {!hideSubtitle && displaySubtitle && (
            <p
              className={`text-white text-xs leading-relaxed max-w-[92%] line-clamp-2 ${hideTitle && hideEyebrow ? "" : "mt-2"}`}
              style={{ textShadow: "0 2px 6px rgba(0,0,0,0.85)" }}
            >
              {displaySubtitle}
            </p>
          )}
        </div>
      )}

      {/* Video controls — auto-hide after 2.5s of no interaction on
          ALL states (playing / paused / muted). Users tap the hero
          anywhere to bring them back — the previous behavior of
          pinning them open whenever `muted` was true never let the
          chrome fade away, because we autoplay muted by default so
          iOS/Chrome allow autoplay. */}
      {showVideo && (
        <div
          className={`absolute z-20 flex items-center gap-1.5 rounded-full bg-black/45 backdrop-blur-sm px-2 py-1.5 ${
            isFs ? "bottom-4 right-4" : "bottom-3 right-3"
          }`}
          style={{
            opacity: controlsVisible ? 1 : 0,
            transition: "opacity 320ms ease",
            pointerEvents: controlsVisible ? "auto" : "none",
          }}
          data-testid={`${testId}-video-controls`}
          onMouseEnter={nudgeControls}
        >
          <button
            type="button"
            onClick={togglePlay}
            aria-label={playing ? "Pause" : "Play"}
            data-testid={`${testId}-video-playpause`}
            className="w-7 h-7 rounded-full grid place-items-center text-white hover:bg-white/15"
          >
            {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
          </button>
          <button
            type="button"
            onClick={toggleMute}
            aria-label={muted ? "Unmute" : "Mute"}
            data-testid={`${testId}-video-mute`}
            className="w-7 h-7 rounded-full grid place-items-center text-white hover:bg-white/15"
          >
            {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
          </button>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={muted ? 0 : volume}
            onChange={onVolume}
            onClick={(e) => e.stopPropagation()}
            aria-label="Volume"
            data-testid={`${testId}-video-volume`}
            className="w-16 accent-white cursor-pointer"
          />
          <button
            type="button"
            onClick={toggleFs}
            aria-label={isFs ? "Exit full screen" : "Full screen"}
            data-testid={`${testId}-video-fullscreen`}
            className="w-7 h-7 rounded-full grid place-items-center text-white hover:bg-white/15"
          >
            {isFs ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          </button>
        </div>
      )}
    </div>
  );
}
